import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, UserPlus, Trash2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient, User } from '../../services/api';

interface ShareReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportId: string;
    reportName: string;
    reportOwnerId: string;
    reportIsOrgWide: boolean;
    currentUserId: string;
    onSuccess: () => void;
}

interface SharedUser {
    id: string;
    sharedWith: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    sharedBy: {
        id: string;
        firstName: string;
        lastName: string;
    };
    sharedAt: string;
    note: string | null;
    canEdit: boolean;
}

const ShareReportModal: React.FC<ShareReportModalProps> = ({
    isOpen,
    onClose,
    reportId,
    reportName,
    reportOwnerId,
    reportIsOrgWide,
    currentUserId,
    onSuccess
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [selectionError, setSelectionError] = useState<string | null>(null);

    // Form state
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [note, setNote] = useState('');
    const [canEdit, setCanEdit] = useState(false);

    // Data state
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [currentShares, setCurrentShares] = useState<SharedUser[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const loadUsers = useCallback(async () => {
        try {
            setIsLoadingUsers(true);
            const response = await apiClient.getUsers({
                isActive: true,
                limit: 1000
            });

            // Filter out current user
            const users = response.data.filter(u => u.id !== currentUserId);
            setAvailableUsers(users);
        } catch (error: any) {
            toast.error('Failed to load users');
            console.error('Error loading users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    }, [currentUserId]);

    const loadCurrentShares = useCallback(async () => {
        try {
            const response = await apiClient.getReportShares(reportId);
            setCurrentShares(response.data);
        } catch (error: any) {
            // If error is 403/404, it might mean user doesn't have permission or no shares exist
            if (error.status === 403 || error.status === 404) {
                setCurrentShares([]);
            } else {
                console.error('Error loading shares:', error);
            }
        }
    }, [reportId]);

    // Load users and current shares when modal opens
    useEffect(() => {
        if (isOpen) {
            loadUsers();
            loadCurrentShares();
        }
    }, [isOpen, loadUsers, loadCurrentShares]);

    const handleUserToggle = (userId: string) => {
        const updated = selectedUserIds.includes(userId)
            ? selectedUserIds.filter(id => id !== userId)
            : [...selectedUserIds, userId];
        setSelectedUserIds(updated);
        if (updated.length > 0) setSelectionError(null);
    };

    const handleShare = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedUserIds.length === 0) {
            setSelectionError('Please select at least one user to share with');
            return;
        }

        try {
            setIsSubmitting(true);
            await apiClient.shareReport(reportId, selectedUserIds, note || undefined, canEdit);

            toast.success(`Report shared with ${selectedUserIds.length} user(s)`);

            // Refresh shares list
            await loadCurrentShares();

            // Reset form
            setSelectedUserIds([]);
            setNote('');
            setCanEdit(false);
            setSelectionError(null);

            onSuccess();
        } catch (error: any) {
            toast.error(error.message || 'Failed to share report');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRevokeShare = async (userId: string, userName: string) => {
        if (!window.confirm(`Are you sure you want to revoke access for ${userName}?`)) {
            return;
        }

        try {
            await apiClient.revokeReportShare(reportId, userId);
            toast.success(`Access revoked for ${userName}`);
            await loadCurrentShares();
            onSuccess();
        } catch (error: any) {
            toast.error(error.message || 'Failed to revoke access');
        }
    };

    // Helper function to get access reason for a user
    const getUserAccessReason = (user: User): string | null => {
        // Check if user is the owner
        if (user.id === reportOwnerId) {
            return 'Creator';
        }

        // Check if report is org-wide
        if (reportIsOrgWide) {
            return 'Org-wide Report';
        }

        // Check if user has privileged role (can see all reports)
        if (user.role === 'admin' || user.role === 'manager' || user.role === 'compliance') {
            return `${user.role.charAt(0).toUpperCase() + user.role.slice(1)} Role`;
        }

        return null;
    };

    // Filter users based on search term and exclude already shared users
    const filteredUsers = availableUsers.filter(user => {
        const matchesSearch = searchTerm === '' ||
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        const alreadyShared = currentShares.some(share => share.sharedWith.id === user.id);

        return matchesSearch && !alreadyShared;
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.1)' }}>
                            <Share2 className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Share Report</h2>
                            <p className="text-sm text-gray-500">{reportName}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="p-6 space-y-6 overflow-y-auto flex-1">
                    {/* Current Shares Section */}
                    {currentShares.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                Currently Shared With ({currentShares.length})
                            </h3>
                            <div className="space-y-2 max-h-40 overflow-y-auto bg-gray-50/80 rounded-lg border border-gray-100 p-3">
                                {currentShares.map((share) => (
                                    <div
                                        key={share.id}
                                        className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                                    >
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                                {share.sharedWith.firstName} {share.sharedWith.lastName}
                                            </p>
                                            <p className="text-xs text-gray-500">{share.sharedWith.email}</p>
                                            {share.note && (
                                                <p className="text-xs text-gray-600 mt-1 italic">
                                                    Note: {share.note}
                                                </p>
                                            )}
                                        </div>
                                        <div className="group relative inline-flex ml-3">
                                            <button
                                                onClick={() => handleRevokeShare(
                                                    share.sharedWith.id,
                                                    `${share.sharedWith.firstName} ${share.sharedWith.lastName}`
                                                )}
                                                className="text-red-600 hover:text-red-800 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                            <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">Revoke access</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Share Form */}
                    <form onSubmit={handleShare} className="space-y-4">
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                Share with Additional Users
                            </h3>

                            {/* Search Input */}
                            <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 mb-3"
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            />

                            {/* User List */}
                            <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                                {isLoadingUsers ? (
                                    <div className="p-4 text-center text-gray-500">
                                        Loading users...
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="p-4 text-center text-gray-500">
                                        {searchTerm ? 'No users found matching your search' : 'No users available to share with'}
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {filteredUsers.map((user) => (
                                            <label
                                                key={user.id}
                                                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUserIds.includes(user.id)}
                                                    onChange={() => handleUserToggle(user.id)}
                                                    className="h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-opacity-50"
                                                    style={{ accentColor: '#69BE28' }}
                                                />
                                                <div className="ml-3 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {user.firstName} {user.lastName}
                                                        </p>
                                                        {getUserAccessReason(user) && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                ✓ {getUserAccessReason(user)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500">{user.email}</p>
                                                    <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {selectionError && (
                                <p className="mt-2 text-sm text-red-600">{selectionError}</p>
                            )}
                            {selectedUserIds.length > 0 && (
                                <p className="mt-2 text-sm text-gray-600">
                                    {selectedUserIds.length} user(s) selected
                                </p>
                            )}
                        </div>

                        {/* Note Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Note (Optional)
                            </label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Add a note about why you're sharing this report..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            />
                        </div>

                        {/* Can Edit Checkbox */}
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                id="canEdit"
                                checked={canEdit}
                                onChange={(e) => setCanEdit(e.target.checked)}
                                className="h-4 w-4 mt-1 rounded border-gray-300 focus:ring-2 focus:ring-opacity-50"
                                style={{ accentColor: '#69BE28' }}
                            />
                            <div className="flex-1">
                                <label htmlFor="canEdit" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    Allow users to edit this report
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Users will be able to regenerate or modify this report
                                </p>
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-600">
                                <p className="font-medium mb-1 text-gray-700">Sharing Information</p>
                                <p>
                                    Shared users will receive a notification and can view this report in their Reports page.
                                    You can revoke access at any time.
                                </p>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                <UserPlus className="w-4 h-4" />
                                <span>{isSubmitting ? 'Sharing...' : 'Share Report'}</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ShareReportModal;
